import { useEffect, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck, CheckCircle2, XCircle, Loader2, Camera, Video, IdCard,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Staff-side ID Verification review panel (inside PatientDetailModal).
 *
 * Identity is now confirmed via our in-house upload: patients submit a
 * government-ID photo + optional selfie video, and staff approve/reject
 * from this panel. Stripe Identity has been removed entirely.
 */
export default function IdVerificationReviewPanel({ patientId }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoSrc, setPhotoSrc] = useState(null);
  const [videoSrc, setVideoSrc] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await API.get(`/admin/users/${patientId}/id-verification`);
      setRecord(data);
      setNotes(data.verification_notes || "");
    } catch {
      setRecord({ has_id_card: false, has_video: false, verified: false, verification_method: "internal_upload" });
    } finally {
      setLoading(false);
    }
  };

  const loadFile = async (kind, setter) => {
    try {
      const res = await API.get(`/admin/users/${patientId}/id-verification/file/${kind}`, { responseType: "blob" });
      setter(URL.createObjectURL(res.data));
    } catch {
      setter(null);
    }
  };

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    setPhotoSrc(null);
    setVideoSrc(null);
    load();
  }, [patientId]); // eslint-disable-line

  useEffect(() => {
    if (record?.has_id_card && !photoSrc) loadFile("id-photo", setPhotoSrc);
    if (record?.has_video && !videoSrc) loadFile("video", setVideoSrc);
    // eslint-disable-next-line
  }, [record]);

  const submit = async (verified) => {
    setSaving(true);
    try {
      await API.post(`/admin/users/${patientId}/id-verification/verify`, { verified, notes });
      toast.success(verified ? "Patient marked as verified" : "Verification rejected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-xs text-slate-400 italic py-4"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Loading verification record…</div>;
  }

  const hasSomething = record?.has_id_card || record?.has_video;

  return (
    <div className="space-y-4" data-testid="id-verify-review-panel">
      {/* Overall status banner */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className={`w-5 h-5 ${record.verified ? "text-emerald-500" : "text-amber-500"}`} />
          <p className="font-semibold">Identity Verification</p>
          <Badge variant="outline" className="text-[10px] text-slate-500 dark:text-slate-400">Internal upload</Badge>
        </div>
        {record.verified ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300" data-testid="id-verify-status-verified">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Verified{record.verified_by_name ? ` by ${record.verified_by_name}` : ""}
            {record.verified_at ? ` · ${new Date(record.verified_at).toLocaleString()}` : ""}
          </Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-700 border-amber-300" data-testid="id-verify-status-pending">
            Not yet verified
          </Badge>
        )}
      </div>

      {!hasSomething ? (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <IdCard className="w-4 h-4 text-purple-600" />
            <p className="font-semibold text-sm">Government ID</p>
          </div>
          <p className="text-xs text-slate-500 italic" data-testid="id-verify-not-started">
            Patient hasn&apos;t uploaded a government ID yet. They&apos;ll be prompted before their first prescription.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Camera className="w-4 h-4 text-slate-500" />
              <p className="font-semibold text-sm">Government ID</p>
            </div>
            {record.has_id_card ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                <div className="text-xs space-y-1">
                  <p><span className="text-slate-500">Type:</span> <b>{record.id_type}</b></p>
                  <p><span className="text-slate-500">Number:</span>{" "}
                    <b className="font-mono" data-testid="id-verify-number">{record.id_number ? record.id_number : `•••• ${record.id_number_last4 || ""}`}</b>
                  </p>
                  {record.id_issuing_state && <p><span className="text-slate-500">State:</span> <b>{record.id_issuing_state}</b></p>}
                </div>
                {photoSrc
                  ? <img src={photoSrc} alt="ID card" className="rounded border border-slate-200 max-h-56 object-contain" data-testid="id-verify-photo" />
                  : <div className="text-xs text-slate-400 italic"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Loading photo…</div>}
              </div>
            ) : <p className="text-xs text-slate-400 italic">No ID photo uploaded.</p>}
          </div>

          {record.has_video && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Video className="w-4 h-4 text-slate-500" />
                <p className="font-semibold text-sm">Video Attestation</p>
              </div>
              {videoSrc
                ? <video src={videoSrc} controls className="w-full max-w-md rounded border border-slate-200" data-testid="id-verify-video" />
                : <div className="text-xs text-slate-400 italic"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Loading video…</div>}
            </div>
          )}
        </>
      )}

      <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3 bg-slate-50 dark:bg-slate-800/30">
        <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">Review decision</p>
        <Textarea
          placeholder="Notes (optional) — e.g. 'Confirmed identity by phone' or 'ID photo too blurry — asked patient to re-upload'"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="text-xs"
          data-testid="id-verify-notes"
        />
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => submit(true)}
            disabled={saving || record.verified}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="id-verify-approve-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Mark as verified
          </Button>
          <Button
            onClick={() => submit(false)}
            disabled={saving}
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="id-verify-reject-btn"
          >
            <XCircle className="w-4 h-4 mr-2" /> Reject / ask to retry
          </Button>
        </div>
      </div>
    </div>
  );
}
